import type { ReactNode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { showToast } from "./components/Toasts";

let root: Root | undefined;
let container: HTMLElement | undefined;
let createApp: (() => ReactNode) | undefined;
let restartPromise: Promise<void> | undefined;
let postRestartTimer: ReturnType<typeof setTimeout> | undefined;

type RestartReact = (delayMs?: number) => Promise<void>;

export function mountReactApp(target: HTMLElement, appFactory: () => ReactNode) {
  container = target;
  createApp = appFactory;
  root = createRoot(target);
  root.render(appFactory());
}

/**
 * Discards React's current Fiber tree and pending update queues, then mounts the app again.
 * Global application state survives; local component/hook state does not.
 */
export const restartReact: RestartReact = (delayMs = 100) => {
  if (restartPromise) return restartPromise;
  console.error("[ReactWatchdog] restarting React - 2");
  if (postRestartTimer) {
    clearTimeout(postRestartTimer);
    postRestartTimer = undefined;
  }

  restartPromise = new Promise<void>((resolve, reject) => {
    // Leave the current React callback/commit before unmounting its root.
    queueMicrotask(() => {
      try {
        console.error("[ReactWatchdog] restarting React - 3");
        if (!container || !createApp) throw new Error("React root has not been initialized");

        root?.unmount();
        container.replaceChildren();

        console.error("[ReactWatchdog] restarting React - 4");
        setTimeout(
          () => {
            try {
              console.error("[ReactWatchdog] restarting React - 5");
              root = createRoot(container!);
              root.render(createApp!());

              console.error("[ReactWatchdog] restarting React - 6");
              // Give the freshly mounted navigation tree and toast host enough time to
              // subscribe before restoring the selection and showing recovery feedback.
              postRestartTimer = setTimeout(() => {
                postRestartTimer = undefined;
                const selectedObject = state.selectionManager.selectedObject;
                state.selectionManager.selectedObject = undefined;
                state.selectionManager.selectedObject = selectedObject;
                void showToast({ text: "Estada wurde neu gestartet", isSuccess: true });
              }, 3_000);

              restartPromise = undefined;
              resolve();
            } catch (error) {
              restartPromise = undefined;
              reject(error);
            }
          },
          Math.max(0, delayMs),
        );
      } catch (error) {
        restartPromise = undefined;
        reject(error);
      }
    });
  });

  return restartPromise;
};

declare global {
  // Available as `restartReact()` in the browser console and for a future watchdog.
  var restartReact: RestartReact;
}

globalThis.restartReact = restartReact;
